import React, { useEffect, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { EPCBadge } from "../components/EPCBadge";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { ArrowUpRight, ArrowDownRight, FilePlus, RefreshCw, AlertTriangle, Building2, TrendingUp, Loader2, FileText, CalendarX, CalendarClock, Mail, Send, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const BAND_COLORS = {
  A: "#008054", B: "#19b459", C: "#8dce46", D: "#ffd500",
  E: "#fcaa65", F: "#ef8023", G: "#e9153b", UNKNOWN: "#cbd5e1",
};

const SEVERITY_COLOR = {
  success: "text-emerald-700 bg-emerald-50 border-emerald-200",
  warning: "text-amber-800 bg-amber-50 border-amber-200",
  info: "text-slate-700 bg-slate-50 border-slate-200",
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [emailDialog, setEmailDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/stats"),
        api.get("/alerts", { params: { limit: 8 } }),
      ]);
      setStats(s.data);
      setAlerts(a.data.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const triggerCheckAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/properties/check-all", null, { params: { limit: 200 } });
      toast.success(`Queued ${data.queued} properties for EPC check`, {
        description: "Refresh in a moment to see new alerts.",
      });
      setTimeout(load, 4000);
    } catch (e) {
      toast.error("Failed to start check");
    } finally {
      setSyncing(false);
    }
  };

  const downloadBoardReport = async () => {
    try {
      const token = localStorage.getItem("epc_token");
      const res = await fetch(`${API_BASE}/reports/board`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Report failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : "EPC-Board-Report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Board Report downloaded");
    } catch {
      toast.error("Failed to generate report");
    }
  };

  if (loading || !stats) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8 fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Operations</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-1">Portfolio Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live status across your social housing stock. {stats.epc_api_configured ? (
              <span className="text-emerald-700 font-medium">EPC API connected.</span>
            ) : (
              <span className="text-amber-700 font-medium">Running in mock mode — add EPC API token in Settings for live data.</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={downloadBoardReport}
            className="rounded-none border-slate-300"
            data-testid="dashboard-board-report"
          >
            <FileText className="h-4 w-4 mr-2" /> Board Report (PDF)
          </Button>
          <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none border-slate-300" data-testid="dashboard-email-board">
                <Mail className="h-4 w-4 mr-2" /> Email Board Report
              </Button>
            </DialogTrigger>
            <EmailBoardDialog onSent={() => setEmailDialog(false)} />
          </Dialog>
          <Link to="/properties">
            <Button variant="outline" className="rounded-none border-slate-300" data-testid="dashboard-import-btn">
              <FilePlus className="h-4 w-4 mr-2" /> Import CSV
            </Button>
          </Link>
          <Button
            onClick={triggerCheckAll}
            disabled={syncing}
            className="rounded-none text-white"
            style={{ backgroundColor: "var(--hi-olive)" }}
            data-testid="dashboard-check-all"
          >
            {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Check EPC Register
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          label="Total Properties"
          value={stats.total_properties.toLocaleString("en-GB")}
          icon={<Building2 className="h-5 w-5" />}
          testid="kpi-total"
        />
        <KPI
          label="Below Band C"
          value={stats.needs_attention.toLocaleString("en-GB")}
          tone="warning"
          icon={<AlertTriangle className="h-5 w-5" />}
          testid="kpi-needs-attention"
          sub={`D-G not in Asana (incl. ${(stats.below_c_standby || 0).toLocaleString("en-GB")} Y2 Standby) · ${(stats.below_c_excluded || 0).toLocaleString("en-GB")} D-G already in Asana / Y1 excluded · ${(stats.unknown_not_in_asana || 0).toLocaleString("en-GB")} Unknown band not counted`}
        />
        <KPI
          label="Changes · 30d"
          value={stats.recent_changes_30d.toLocaleString("en-GB")}
          icon={<TrendingUp className="h-5 w-5" />}
          testid="kpi-recent-changes"
        />
        <KPI
          label="Unread Alerts"
          value={stats.unread_alerts.toLocaleString("en-GB")}
          tone={stats.unread_alerts > 0 ? "info" : null}
          icon={<ArrowUpRight className="h-5 w-5" />}
          testid="kpi-unread"
        />
        <KPI
          label="Heritage coverage"
          value={`${stats.heritage_scanned !== undefined && stats.total_properties ? Math.round((stats.heritage_scanned / stats.total_properties) * 100) : 0}%`}
          tone={stats.heritage_pending && stats.total_properties && (stats.heritage_pending / stats.total_properties) > 0.1 ? "warning" : null}
          icon={<ShieldCheck className="h-5 w-5" />}
          testid="kpi-heritage-coverage"
          to={stats.heritage_pending > 0 ? "/properties?heritage_pending=true" : "/properties"}
          sub={
            stats.heritage_pending !== undefined
              ? `${(stats.heritage_scanned || 0).toLocaleString("en-GB")} of ${(stats.total_properties || 0).toLocaleString("en-GB")} scanned · ${(stats.heritage_pending || 0).toLocaleString("en-GB")} pending`
              : null
          }
        />
      </div>

      {/* Expiry pulse — two large clickable tiles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Link
          to="/properties?expired_only=true"
          className="border-2 p-6 flex items-start justify-between group transition-colors hover:bg-red-50/40"
          style={{ borderColor: "#dc2626", backgroundColor: "#fef2f2" }}
          data-testid="kpi-expired"
        >
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-red-800">
              <CalendarX className="h-4 w-4" />
              Expired EPC Certificates
            </div>
            <div className="font-heading font-black text-5xl mt-3 tracking-tight text-red-800">
              {(stats.expired_certificates ?? 0).toLocaleString("en-GB")}
            </div>
            <div className="text-xs text-red-900/70 mt-2 leading-snug max-w-sm">
              Properties whose current EPC has passed its 10-year validity. These should be re-assessed —
              click to see the full list and prioritise re-inspection.
            </div>
          </div>
          <ArrowUpRight className="h-5 w-5 text-red-700 group-hover:translate-x-1 transition-transform" />
        </Link>

        <Link
          to="/properties?expiring_12m=true"
          className="border-2 p-6 flex items-start justify-between group transition-colors hover:bg-amber-50/40"
          style={{ borderColor: "var(--hi-orange)", backgroundColor: "#fff8ed" }}
          data-testid="kpi-expiring-12m"
        >
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold" style={{ color: "var(--hi-orange-deep)" }}>
              <CalendarClock className="h-4 w-4" />
              Expiring within 12 months
            </div>
            <div className="font-heading font-black text-5xl mt-3 tracking-tight" style={{ color: "var(--hi-orange-deep)" }}>
              {(stats.expiring_12_months ?? 0).toLocaleString("en-GB")}
            </div>
            <div className="text-xs mt-2 leading-snug max-w-sm" style={{ color: "var(--hi-orange-deep)" }}>
              Active certificates approaching their 10-year expiry. Plan your retrofit programme around these
              addresses before they fall off the register.
            </div>
          </div>
          <ArrowUpRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" style={{ color: "var(--hi-orange)" }} />
        </Link>
      </div>

      {/* Distribution + Alerts feed */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 rounded-none border-border p-6" data-testid="band-distribution-chart">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Distribution</div>
              <h2 className="font-heading text-2xl font-bold mt-1">EPC Band Coverage</h2>
            </div>
            <div className="flex gap-3 flex-wrap">
              {stats.band_distribution.map((b) => (
                <div key={b.band} className="flex items-center gap-1.5 text-xs">
                  <span className="inline-block h-3 w-3" style={{ backgroundColor: BAND_COLORS[b.band] }} />
                  <span className="font-mono">{b.band}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={256}>
              <BarChart data={stats.band_distribution} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="band" tick={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{ borderRadius: 0, border: "1px solid #e2e8f0", fontFamily: "IBM Plex Sans" }}
                  formatter={(v) => [`${v} properties`, "Count"]}
                />
                <Bar dataKey="count" radius={0}>
                  {stats.band_distribution.map((entry, idx) => (
                    <Cell key={idx} fill={BAND_COLORS[entry.band] || "#cbd5e1"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-none border-border p-6 flex flex-col" data-testid="recent-alerts-feed">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Live feed</div>
              <h2 className="font-heading text-2xl font-bold mt-1">Recent Alerts</h2>
            </div>
            <Link to="/alerts" className="text-xs uppercase tracking-wider font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1">
              All <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="flex-1 space-y-2 overflow-auto max-h-72">
            {alerts.length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">
                No alerts yet. Trigger a check to scan the register.
              </div>
            )}
            {alerts.map((a) => (
              <div
                key={a.id}
                className={`border-l-2 pl-3 py-2 ${SEVERITY_COLOR[a.severity] || SEVERITY_COLOR.info}`}
                data-testid={`alert-feed-item-${a.id}`}
              >
                <div className="flex items-center gap-2">
                  {a.type === "band_upgrade" && <ArrowUpRight className="h-3.5 w-3.5" />}
                  {a.type === "band_downgrade" && <ArrowDownRight className="h-3.5 w-3.5" />}
                  <span className="text-xs uppercase tracking-wider font-semibold">{a.type.replace("_", " ")}</span>
                </div>
                <div className="text-sm font-medium mt-1 truncate">{a.address}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{a.message}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Band overview row */}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Quick filter by band</div>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
          {stats.band_distribution.map((b) => (
            <Link
              key={b.band}
              to={`/properties?band=${b.band}`}
              className="border border-border p-3 hover:border-slate-900 transition-colors group"
              data-testid={`band-tile-${b.band}`}
            >
              <div className="flex items-center justify-between mb-1">
                <EPCBadge band={b.band} size="sm" />
                <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="font-heading font-black text-2xl mt-2">{b.count.toLocaleString("en-GB")}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">properties</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon, tone, testid, to }) {
  const toneClass = tone === "warning"
    ? "border-amber-300 bg-amber-50/40"
    : tone === "info"
      ? "border-slate-300 bg-slate-50/40"
      : "border-border bg-white";
  const body = (
    <Card className={`rounded-none p-5 ${toneClass} ${to ? "cursor-pointer hover:shadow-md hover:-translate-y-px transition-all" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</div>
        <div className="text-muted-foreground">{icon}</div>
      </div>
      <div className="font-heading font-black text-4xl mt-3 tracking-tight">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}


function EmailBoardDialog({ onSent }) {
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const list = recipients.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error("Add at least one recipient email");
      return;
    }
    if (list.length > 10) {
      toast.error("Maximum 10 recipients per send");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/reports/board/email", { recipients: list, message: message || null });
      setResults(data);
      toast.success(`Board Report sent · ${data.size_kb} KB attached`, {
        description: `Provider: ${data.provider}${data.provider === "mock" ? " (logged to email_log)" : ""}`,
      });
    } catch (e) {
      toast.error("Email failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <DialogContent className="rounded-none">
      <DialogHeader>
        <DialogTitle>Email Board Report</DialogTitle>
        <DialogDescription>
          Generates a fresh PDF and emails it as an attachment to up to 10 recipients.
        </DialogDescription>
      </DialogHeader>
      {results ? (
        <div className="space-y-3" data-testid="email-results">
          <div className="border border-emerald-300 bg-emerald-50 p-3 text-xs">
            <div className="font-bold text-emerald-900">Email sent ✓</div>
            <div className="text-emerald-800/80 mt-1">PDF size: {results.size_kb} KB · provider: <span className="font-mono">{results.provider}</span></div>
          </div>
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left py-1">Recipient</th><th className="text-left py-1">Provider</th><th className="text-left py-1">Status</th></tr>
            </thead>
            <tbody>
              {(results.results || []).map((r) => (
                <tr key={r.to} className="border-t border-slate-100">
                  <td className="py-1.5 font-mono">{r.to}</td>
                  <td className="py-1.5">{r.provider}</td>
                  <td className="py-1.5"><span className={r.status === "sent" ? "text-emerald-700" : "text-red-700"}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <DialogFooter>
            <Button onClick={onSent} className="rounded-none bg-slate-900" data-testid="email-done">Done</Button>
          </DialogFooter>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="email-board-form">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Recipients (comma or newline-separated)</Label>
            <Textarea rows={3} value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="board-chair@housing.org, finance@housing.org" className="rounded-none border-slate-300 font-mono text-xs" data-testid="email-recipients" required />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Message (optional)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Briefly note the period covered or any key headlines…" className="rounded-none border-slate-300" data-testid="email-message" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={sending} className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="submit-email-board">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Send report
            </Button>
          </DialogFooter>
        </form>
      )}
    </DialogContent>
  );
}
